import {useForm} from "@mantine/form";
import {Box, Button, Group} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DateInput} from "@mantine/dates";
import {useCallback} from "react";
import {isBefore} from "@ots-frontend/ots-data";

export type FromToDateFormValues = {
  fromDate?: Date
  toDate?: Date
}

export type FromToDateFormProps = {
  onSubmit: (values: FromToDateFormValues) => void;
  defaultValues?: FromToDateFormValues
  requireFromDttm?: boolean;
  requireToDttm?: boolean;
  loading?: boolean;
};

export const FromToDateForm = (props: FromToDateFormProps) => {
  const {
    onSubmit,
    defaultValues,
    requireFromDttm = true,
    requireToDttm = false,
    loading = false,
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      fromDate: defaultValues?.fromDate,
      toDate: defaultValues?.toDate
    },

    validate: {
      fromDate: (value) => requireFromDttm && !value ? t("common.form.validation.required", "Required") : undefined,
      toDate: (value, values) => {
        let error: string | undefined = undefined
        if (requireToDttm && !value) {
          error = t<string>("ots.ui.validation.required", "Required")
        }

        if (!error && value && values.fromDate && isBefore(value, values.fromDate)) {
          error = t<string>("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }

        return error
      }
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    onSubmit({
      fromDate: values.fromDate,
      toDate: values.toDate
    });
  }, [form, onSubmit]);

  return (
    <Box mx="auto">
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Group mt="xl" align="flex-end">
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("ots.ui.shared.fromtodate.form.fromdate", "From Date")}
            popoverProps={{
              withinPortal: true
            }}
            styles={{wrapper: {width: "200px"}}}
            clearable={true}
            withAsterisk={requireFromDttm}
            {...form.getInputProps("fromDate")}
          />
          <DateInput
            valueFormat="MM/DD/YYYY HH:mm"
            label={t("ots.ui.shared.fromtodate.form.todate", "To Date")}
            popoverProps={{
              withinPortal: true
            }}
            styles={{wrapper: {width: "200px"}}}
            clearable={true}
            withAsterisk={requireToDttm}
            {...form.getInputProps("toDate")}
          />
          <Button loading={loading} type="submit">
            {t("ots.ui.actions.submit", "Submit")}
          </Button>
        </Group>
      </form>
    </Box>
  );
};
